import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import {
  X,
  Plane,
  Receipt,
  Building2,
  Calendar,
  DollarSign,
  Paperclip,
  Loader2,
  CheckCircle2,
  AlertCircle,
  FileText,
  MapPin,
  Tag
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function SelfBookingClaimModal({ isOpen, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);

  // Form State
  const [form, setForm] = useState({
    purpose: '',
    from_city: '',
    to_city: '',
    start_date: new Date().toISOString().slice(0, 10),
    end_date: new Date().toISOString().slice(0, 10),
    travel_mode: 'flight',
    department: 'General',
    project: 'N/A',

    // Itemized Expense Categories
    ticket_amount: '',
    ticket_date: new Date().toISOString().slice(0, 10),
    ticket_remarks: '',
    ticket_vendor_ref: '',
    ticket_attachment: '',

    hotel_amount: '',
    hotel_date: new Date().toISOString().slice(0, 10),
    hotel_remarks: '',
    hotel_vendor_ref: '',
    hotel_attachment: '',

    food_amount: '',
    food_date: new Date().toISOString().slice(0, 10),
    food_remarks: '',
    food_attachment: '',

    local_travel_amount: '',
    local_travel_date: new Date().toISOString().slice(0, 10),
    local_travel_remarks: '',
    local_travel_attachment: '',

    misc_amount: '',
    misc_date: new Date().toISOString().slice(0, 10),
    misc_remarks: '',
    misc_attachment: '',
  });

  if (!isOpen) return null;

  // Calculate dynamic Total Claim Amount
  const ticketAmt = parseFloat(form.ticket_amount) || 0;
  const hotelAmt = parseFloat(form.hotel_amount) || 0;
  const foodAmt = parseFloat(form.food_amount) || 0;
  const localAmt = parseFloat(form.local_travel_amount) || 0;
  const miscAmt = parseFloat(form.misc_amount) || 0;

  const totalClaimAmount = roundTwoDecimals(ticketAmt + hotelAmt + foodAmt + localAmt + miscAmt);

  function roundTwoDecimals(val) {
    return Math.round((val + Number.EPSILON) * 100) / 100;
  }

  const handleSubmit = async (targetStatus = 'submitted') => {
    if (targetStatus === 'submitted') {
      if (!form.purpose.trim()) {
        toast.error('Please enter Travel Purpose / Title.');
        return;
      }
      if (!form.from_city.trim() || !form.to_city.trim()) {
        toast.error('Please enter From City and To City.');
        return;
      }
      if (totalClaimAmount <= 0) {
        toast.error('Please enter an expense amount for at least one category.');
        return;
      }
    }

    try {
      setLoading(true);
      const allAttachments = [
        form.ticket_attachment,
        form.hotel_attachment,
        form.food_attachment,
        form.local_travel_attachment,
        form.misc_attachment,
      ].filter((url) => url && url.trim().length > 0);

      const payload = {
        purpose: form.purpose.trim(),
        trip_title: form.purpose.trim(),
        origin_city: form.from_city.trim(),
        destination_city: form.to_city.trim(),
        start_date: form.start_date,
        end_date: form.end_date,
        travel_mode: form.travel_mode,
        department: form.department,
        project: form.project,

        ticket_amount: ticketAmt,
        ticket_date: form.ticket_date,
        ticket_remarks: form.ticket_remarks,
        ticket_vendor_ref: form.ticket_vendor_ref,
        ticket_attachments: form.ticket_attachment ? [form.ticket_attachment] : [],

        hotel_amount: hotelAmt,
        hotel_date: form.hotel_date,
        hotel_remarks: form.hotel_remarks,
        hotel_vendor_ref: form.hotel_vendor_ref,
        hotel_attachments: form.hotel_attachment ? [form.hotel_attachment] : [],

        food_amount: foodAmt,
        food_date: form.food_date,
        food_remarks: form.food_remarks,
        food_attachments: form.food_attachment ? [form.food_attachment] : [],

        local_travel_amount: localAmt,
        local_travel_date: form.local_travel_date,
        local_travel_remarks: form.local_travel_remarks,
        local_travel_attachments: form.local_travel_attachment ? [form.local_travel_attachment] : [],

        misc_amount: miscAmt,
        misc_date: form.misc_date,
        misc_remarks: form.misc_remarks,
        misc_attachments: form.misc_attachment ? [form.misc_attachment] : [],

        total_claim_amount: totalClaimAmount,
        all_attachments: allAttachments,
        attachments: allAttachments,
        status: targetStatus,
      };

      const { data } = await axios.post(`${API_URL}/travel-desk/self-booking-claims`, payload, {
        withCredentials: true,
      });

      if (data.success || data.message) {
        toast.success(
          targetStatus === 'draft'
            ? 'Self Booking Claim saved as draft!'
            : 'Self Booking Reimbursement Claim submitted successfully!'
        );
        if (onSuccess) onSuccess();
        onClose();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit Self Booking claim');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 backdrop-blur-sm" data-testid="self-booking-modal">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[92vh] overflow-y-auto shadow-2xl p-6 border border-slate-200 space-y-6">
        
        {/* HEADER */}
        <div className="flex items-center justify-between border-b border-slate-200 pb-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-indigo-100 text-indigo-700 rounded-xl border border-indigo-200">
              <Receipt className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-lg font-black text-slate-900">Self Booking — Travel Expense Claim</h2>
              <p className="text-xs text-slate-500">
                Submit actual travel expenses &amp; bills arranged directly for corporate reimbursement.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 p-1 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* SECTION 1: TRAVEL DETAILS */}
        <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3 text-xs">
          <h3 className="font-bold uppercase text-slate-700 tracking-wider flex items-center gap-1.5">
            <MapPin className="w-4 h-4 text-indigo-600" /> 1. Travel Details &amp; Purpose
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-3">
              <label className="block font-bold text-slate-700 mb-1">Travel Purpose / Claim Title *</label>
              <input
                type="text"
                placeholder="e.g. Client Onboarding Meeting & Site Visit — Mumbai"
                value={form.purpose}
                onChange={(e) => setForm({ ...form, purpose: e.target.value })}
                className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-xs outline-none focus:border-indigo-500 font-semibold"
                required
              />
            </div>

            <div>
              <label className="block font-bold text-slate-700 mb-1">From City *</label>
              <input
                type="text"
                placeholder="e.g. Bengaluru"
                value={form.from_city}
                onChange={(e) => setForm({ ...form, from_city: e.target.value })}
                className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-xs"
                required
              />
            </div>

            <div>
              <label className="block font-bold text-slate-700 mb-1">To City *</label>
              <input
                type="text"
                placeholder="e.g. Mumbai"
                value={form.to_city}
                onChange={(e) => setForm({ ...form, to_city: e.target.value })}
                className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-xs"
                required
              />
            </div>

            <div>
              <label className="block font-bold text-slate-700 mb-1">Primary Travel Mode</label>
              <select
                value={form.travel_mode}
                onChange={(e) => setForm({ ...form, travel_mode: e.target.value })}
                className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-xs font-medium cursor-pointer"
              >
                <option value="flight">Flight</option>
                <option value="train">Train</option>
                <option value="cab">Cab / Taxi</option>
                <option value="bus">Bus</option>
                <option value="own_vehicle">Own Vehicle</option>
              </select>
            </div>

            <div>
              <label className="block font-bold text-slate-700 mb-1">Start Date *</label>
              <input
                type="date"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-xs"
              />
            </div>

            <div>
              <label className="block font-bold text-slate-700 mb-1">End Date *</label>
              <input
                type="date"
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-xs"
              />
            </div>

            <div>
              <label className="block font-bold text-slate-700 mb-1">Department</label>
              <input
                type="text"
                value={form.department}
                onChange={(e) => setForm({ ...form, department: e.target.value })}
                className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-xs"
              />
            </div>
          </div>
        </div>

        {/* SECTION 2: ITEMIZED EXPENSE CATEGORIES */}
        <div className="space-y-4 text-xs">
          <h3 className="font-bold uppercase text-slate-700 tracking-wider flex items-center gap-1.5">
            <DollarSign className="w-4 h-4 text-emerald-600" /> 2. Expense Category Breakdown &amp; Bills
          </h3>

          {/* 1. Travel Ticket */}
          <div className="bg-white border border-slate-200 rounded-xl p-3.5 space-y-2.5 shadow-sm">
            <div className="flex items-center justify-between font-bold text-slate-800 text-xs">
              <span className="flex items-center gap-1.5 text-indigo-700">
                <Plane className="w-4 h-4" /> 1. Travel Ticket / Flight / Train / Bus
              </span>
              <span className="font-mono text-emerald-700">₹ {ticketAmt.toLocaleString('en-IN')}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2.5">
              <div>
                <label className="block text-[10px] font-bold text-slate-500">Amount (₹)</label>
                <input
                  type="number"
                  placeholder="0.00"
                  value={form.ticket_amount}
                  onChange={(e) => setForm({ ...form, ticket_amount: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-2.5 py-1.5 font-mono font-bold"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-500">Expense Date</label>
                <input
                  type="date"
                  value={form.ticket_date}
                  onChange={(e) => setForm({ ...form, ticket_date: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-2.5 py-1.5"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-500">PNR / PNR Booking Ref</label>
                <input
                  type="text"
                  placeholder="PNR / Ticket #"
                  value={form.ticket_vendor_ref}
                  onChange={(e) => setForm({ ...form, ticket_vendor_ref: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-2.5 py-1.5 font-mono uppercase"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-500">Bill Attachment URL</label>
                <input
                  type="text"
                  placeholder="https://... ticket_pdf"
                  value={form.ticket_attachment}
                  onChange={(e) => setForm({ ...form, ticket_attachment: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-2.5 py-1.5"
                />
              </div>
            </div>
          </div>

          {/* 2. Hotel */}
          <div className="bg-white border border-slate-200 rounded-xl p-3.5 space-y-2.5 shadow-sm">
            <div className="flex items-center justify-between font-bold text-slate-800 text-xs">
              <span className="flex items-center gap-1.5 text-blue-700">
                <Building2 className="w-4 h-4" /> 2. Hotel / Accommodation
              </span>
              <span className="font-mono text-emerald-700">₹ {hotelAmt.toLocaleString('en-IN')}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2.5">
              <div>
                <label className="block text-[10px] font-bold text-slate-500">Amount (₹)</label>
                <input
                  type="number"
                  placeholder="0.00"
                  value={form.hotel_amount}
                  onChange={(e) => setForm({ ...form, hotel_amount: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-2.5 py-1.5 font-mono font-bold"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-500">Expense Date</label>
                <input
                  type="date"
                  value={form.hotel_date}
                  onChange={(e) => setForm({ ...form, hotel_date: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-2.5 py-1.5"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-500">Hotel Name &amp; Invoice #</label>
                <input
                  type="text"
                  placeholder="Hotel Taj - Inv #102"
                  value={form.hotel_vendor_ref}
                  onChange={(e) => setForm({ ...form, hotel_vendor_ref: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-2.5 py-1.5 font-medium"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-500">Bill Attachment URL</label>
                <input
                  type="text"
                  placeholder="https://... hotel_invoice"
                  value={form.hotel_attachment}
                  onChange={(e) => setForm({ ...form, hotel_attachment: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-2.5 py-1.5"
                />
              </div>
            </div>
          </div>

          {/* 3. Food / Meals */}
          <div className="bg-white border border-slate-200 rounded-xl p-3.5 space-y-2.5 shadow-sm">
            <div className="flex items-center justify-between font-bold text-slate-800 text-xs">
              <span className="flex items-center gap-1.5 text-amber-700">
                <Receipt className="w-4 h-4" /> 3. Food &amp; Meals
              </span>
              <span className="font-mono text-emerald-700">₹ {foodAmt.toLocaleString('en-IN')}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
              <div>
                <label className="block text-[10px] font-bold text-slate-500">Amount (₹)</label>
                <input
                  type="number"
                  placeholder="0.00"
                  value={form.food_amount}
                  onChange={(e) => setForm({ ...form, food_amount: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-2.5 py-1.5 font-mono font-bold"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-500">Expense Date</label>
                <input
                  type="date"
                  value={form.food_date}
                  onChange={(e) => setForm({ ...form, food_date: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-2.5 py-1.5"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-500">Bill Attachment URL</label>
                <input
                  type="text"
                  placeholder="https://... food_receipt"
                  value={form.food_attachment}
                  onChange={(e) => setForm({ ...form, food_attachment: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-2.5 py-1.5"
                />
              </div>
            </div>
          </div>

          {/* 4. Local Conveyance */}
          <div className="bg-white border border-slate-200 rounded-xl p-3.5 space-y-2.5 shadow-sm">
            <div className="flex items-center justify-between font-bold text-slate-800 text-xs">
              <span className="flex items-center gap-1.5 text-purple-700">
                <MapPin className="w-4 h-4" /> 4. Local Travel &amp; Conveyance (Cab / Auto)
              </span>
              <span className="font-mono text-emerald-700">₹ {localAmt.toLocaleString('en-IN')}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
              <div>
                <label className="block text-[10px] font-bold text-slate-500">Amount (₹)</label>
                <input
                  type="number"
                  placeholder="0.00"
                  value={form.local_travel_amount}
                  onChange={(e) => setForm({ ...form, local_travel_amount: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-2.5 py-1.5 font-mono font-bold"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-500">Expense Date</label>
                <input
                  type="date"
                  value={form.local_travel_date}
                  onChange={(e) => setForm({ ...form, local_travel_date: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-2.5 py-1.5"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-500">Bill Attachment URL</label>
                <input
                  type="text"
                  placeholder="https://... cab_receipt"
                  value={form.local_travel_attachment}
                  onChange={(e) => setForm({ ...form, local_travel_attachment: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-2.5 py-1.5"
                />
              </div>
            </div>
          </div>

          {/* 5. Miscellaneous */}
          <div className="bg-white border border-slate-200 rounded-xl p-3.5 space-y-2.5 shadow-sm">
            <div className="flex items-center justify-between font-bold text-slate-800 text-xs">
              <span className="flex items-center gap-1.5 text-slate-700">
                <Tag className="w-4 h-4" /> 5. Miscellaneous / Other Official Expenses
              </span>
              <span className="font-mono text-emerald-700">₹ {miscAmt.toLocaleString('en-IN')}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
              <div>
                <label className="block text-[10px] font-bold text-slate-500">Amount (₹)</label>
                <input
                  type="number"
                  placeholder="0.00"
                  value={form.misc_amount}
                  onChange={(e) => setForm({ ...form, misc_amount: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-2.5 py-1.5 font-mono font-bold"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-500">Expense Date</label>
                <input
                  type="date"
                  value={form.misc_date}
                  onChange={(e) => setForm({ ...form, misc_date: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-2.5 py-1.5"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-500">Bill Attachment URL</label>
                <input
                  type="text"
                  placeholder="https://... misc_receipt"
                  value={form.misc_attachment}
                  onChange={(e) => setForm({ ...form, misc_attachment: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-2.5 py-1.5"
                />
              </div>
            </div>
          </div>
        </div>

        {/* SUMMARY LIVE CALCULATION BOX */}
        <div className="bg-gradient-to-r from-emerald-900 to-teal-950 text-white p-4 rounded-xl shadow-md flex items-center justify-between">
          <div>
            <span className="text-[10px] uppercase font-bold tracking-wider text-emerald-200">Total Calculated Claim Amount</span>
            <p className="text-2xl font-black font-mono">₹ {totalClaimAmount.toLocaleString('en-IN')}</p>
          </div>
          <div className="text-right text-[11px] text-emerald-200">
            <p>Ticket: ₹{ticketAmt} &middot; Hotel: ₹{hotelAmt}</p>
            <p>Food: ₹{foodAmt} &middot; Local: ₹{localAmt} &middot; Misc: ₹{miscAmt}</p>
          </div>
        </div>

        {/* ACTIONS */}
        <div className="flex items-center justify-between pt-2 border-t border-slate-200">
          <Button
            type="button"
            variant="outline"
            onClick={() => handleSubmit('draft')}
            disabled={loading}
            className="text-slate-600 border-slate-300 text-xs font-bold"
          >
            Save Draft
          </Button>

          <div className="flex gap-2">
            <Button type="button" variant="ghost" onClick={onClose} className="text-xs">
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => handleSubmit('submitted')}
              disabled={loading}
              className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold shadow-md"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null} Submit Self Booking Claim
            </Button>
          </div>
        </div>

      </div>
    </div>
  );
}
